/**
 * CRITICAL: AUTH MONITORING SYSTEM - DO NOT MODIFY
 * 
 * This system monitors auth health and detects tampering attempts.
 * It is IMMUTABLE and cannot be disabled.
 * 
 * RULES:
 * 1. NO feature can disable monitoring
 * 2. NO modifications without security review
 * 3. ALWAYS logs security events
 * 4. NO external dependencies except console
 */

import { AuthCore, AuthState } from './AuthCore';
import { AuthProtection } from './AuthCircuitBreaker';

/**
 * Auth monitoring event types
 */
type AuthMonitoringEvent = 
  | { type: 'AUTH_SUCCESS'; userId: string; timestamp: number }
  | { type: 'AUTH_FAILURE'; reason: string; timestamp: number }
  | { type: 'AUTH_TAMPERING_DETECTED'; details: string; timestamp: number }
  | { type: 'AUTH_CIRCUIT_OPENED'; reason: string; timestamp: number }
  | { type: 'AUTH_SUSPICIOUS_ACTIVITY'; activity: string; timestamp: number }
  | { type: 'AUTH_CORE_ACCESSED'; accessor: string; timestamp: number };

/**
 * Auth health metrics
 */
interface AuthHealthMetrics {
  totalLogins: number;
  failedLogins: number;
  successRate: number;
  lastLoginTime: number;
  circuitBreakerTrips: number;
  tamperingAttempts: number;
  suspiciousActivities: number;
}

/**
 * Security alert levels
 */
type SecurityAlertLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

interface SecurityAlert {
  readonly level: SecurityAlertLevel;
  readonly message: string;
  readonly timestamp: number;
  readonly details?: any;
}

/**
 * IMMUTABLE AUTH MONITORING SYSTEM
 * 
 * Monitors auth system health and security.
 * Cannot be disabled or bypassed.
 */
class AuthMonitoring {
  private static instance: AuthMonitoring | null = null;
  private events: AuthMonitoringEvent[] = [];
  private alerts: SecurityAlert[] = [];
  private metrics: AuthHealthMetrics = {
    totalLogins: 0,
    failedLogins: 0,
    successRate: 100,
    lastLoginTime: 0,
    circuitBreakerTrips: 0,
    tamperingAttempts: 0,
    suspiciousActivities: 0
  };
  
  private readonly maxEvents = 1000;
  private readonly maxAlerts = 100;
  private monitoring = false; // Temporarily disabled to prevent false positives

  private constructor() {
    this.startMonitoring();
    
    // Seal the instance
    Object.seal(this);
  }

  /**
   * Singleton pattern
   */
  public static getInstance(): AuthMonitoring {
    if (!AuthMonitoring.instance) {
      AuthMonitoring.instance = new AuthMonitoring();
    }
    return AuthMonitoring.instance;
  }

  /**
   * Start monitoring auth system
   */
  private startMonitoring(): void {
    // Monitor auth state changes
    AuthCore.getInstance().subscribe((state: AuthState) => {
      this.handleAuthStateChange(state);
    });

    // Monitor circuit breaker status
    setInterval(() => {
      this.checkCircuitBreakerStatus();
    }, 10000); // Check every 10 seconds

    // Monitor for tampering attempts
    this.setupTamperingDetection();

    // Periodic health check
    setInterval(() => {
      this.performHealthCheck();
    }, 60000); // Check every minute

  }

  /**
   * Log auth monitoring event
   */
  public logEvent(event: AuthMonitoringEvent): void {
    if (!this.monitoring) return;

    // Add to events list
    this.events.push(event);
    
    // Maintain max events limit
    if (this.events.length > this.maxEvents) {
      this.events = this.events.slice(-this.maxEvents);
    }

    // Update metrics
    this.updateMetrics(event);

    // Check for security issues
    this.analyzeEvent(event);

  }

  /**
   * Create security alert
   */
  public createAlert(level: SecurityAlertLevel, message: string, details?: any): void {
    const alert: SecurityAlert = {
      level,
      message,
      timestamp: Date.now(),
      details
    };

    this.alerts.push(alert);
    
    // Maintain max alerts limit
    if (this.alerts.length > this.maxAlerts) {
      this.alerts = this.alerts.slice(-this.maxAlerts);
    }

    // Log critical alerts
    if (level === 'CRITICAL' || level === 'HIGH') {
      console.error('🚨 SECURITY ALERT:', alert);
    } else {
      console.warn('⚠️ Security Warning:', alert);
    }

    // Take action for critical alerts
    if (level === 'CRITICAL') {
      this.handleCriticalAlert(alert);
    }
  }

  /**
   * Get current auth health metrics
   */
  public getHealthMetrics(): Readonly<AuthHealthMetrics> {
    return Object.freeze({ ...this.metrics });
  }

  /**
   * Get recent security alerts
   */
  public getRecentAlerts(limit = 10): readonly SecurityAlert[] {
    return Object.freeze(this.alerts.slice(-limit));
  }

  /**
   * Get recent events
   */
  public getRecentEvents(limit = 50): readonly AuthMonitoringEvent[] {
    return Object.freeze(this.events.slice(-limit));
  }

  /**
   * Check if auth system is healthy
   */
  public isHealthy(): boolean {
    const recentFailures = this.getRecentFailures();
    const circuitStatus = AuthProtection.getStatus();
    
    return (
      this.metrics.successRate > 80 &&
      recentFailures < 5 &&
      circuitStatus.state !== 'OPEN' &&
      this.metrics.tamperingAttempts === 0
    );
  }

  /**
   * Handle auth state changes
   */
  private handleAuthStateChange(state: AuthState): void {
    if (state.isAuthenticated && state.user) {
      this.logEvent({
        type: 'AUTH_SUCCESS',
        userId: state.user.id,
        timestamp: Date.now()
      });
    } else if (!state.isLoading && !state.isAuthenticated) {
      // Only log failure if not in loading state
      this.logEvent({
        type: 'AUTH_FAILURE',
        reason: 'User not authenticated',
        timestamp: Date.now()
      });
    }
  }

  /**
   * Check circuit breaker status
   */
  private checkCircuitBreakerStatus(): void {
    const status = AuthProtection.getStatus();
    
    if (status.state === 'OPEN') {
      this.logEvent({
        type: 'AUTH_CIRCUIT_OPENED',
        reason: `Circuit opened with ${status.failureCount} failures`,
        timestamp: Date.now()
      });
      
      this.createAlert(
        'HIGH',
        'Auth circuit breaker opened',
        { status }
      );
    }
  }

  /**
   * Setup tampering detection
   */
  private setupTamperingDetection(): void {
    // Monitor for direct access to auth core
    const authCoreInstance = AuthCore.getInstance();
    const originalGetState = authCoreInstance.getState;
    (authCoreInstance as any).getState = () => {
      this.logEvent({
        type: 'AUTH_CORE_ACCESSED',
        accessor: 'getState',
        timestamp: Date.now()
      });
      return originalGetState.call(authCoreInstance);
    };

    // Monitor for suspicious localStorage access
    if (typeof window !== 'undefined') {
      const originalSetItem = localStorage.setItem;
      localStorage.setItem = (key: string, value: string) => {
        if (key.includes('auth') || key.includes('token') || key.includes('user')) {
          this.logEvent({
            type: 'AUTH_SUSPICIOUS_ACTIVITY',
            activity: `localStorage.setItem called with auth-related key: ${key}`,
            timestamp: Date.now()
          });
        }
        return originalSetItem.call(localStorage, key, value);
      };
    }
  }

  /**
   * Update metrics based on event
   */
  private updateMetrics(event: AuthMonitoringEvent): void {
    switch (event.type) {
      case 'AUTH_SUCCESS':
        this.metrics.totalLogins++;
        this.metrics.lastLoginTime = event.timestamp;
        break;
        
      case 'AUTH_FAILURE':
        this.metrics.failedLogins++;
        break;
        
      case 'AUTH_CIRCUIT_OPENED':
        this.metrics.circuitBreakerTrips++;
        break;
        
      case 'AUTH_TAMPERING_DETECTED':
        this.metrics.tamperingAttempts++;
        break;
        
      case 'AUTH_SUSPICIOUS_ACTIVITY':
        this.metrics.suspiciousActivities++;
        break;
    }

    // Update success rate
    if (this.metrics.totalLogins > 0) {
      this.metrics.successRate = 
        ((this.metrics.totalLogins - this.metrics.failedLogins) / this.metrics.totalLogins) * 100;
    }
  }

  /**
   * Analyze event for security issues
   */
  private analyzeEvent(event: AuthMonitoringEvent): void {
    switch (event.type) {
      case 'AUTH_TAMPERING_DETECTED':
        this.createAlert('CRITICAL', 'Auth tampering detected', event);
        break;
        
      case 'AUTH_CIRCUIT_OPENED':
        this.createAlert('HIGH', 'Auth circuit breaker opened', event);
        break;
        
      case 'AUTH_SUSPICIOUS_ACTIVITY':
        this.createAlert('MEDIUM', 'Suspicious auth activity detected', event);
        break;
        
      case 'AUTH_FAILURE':
        const recentFailures = this.getRecentFailures();
        if (recentFailures > 3) {
          this.createAlert('MEDIUM', `Multiple auth failures: ${recentFailures}`, event);
        }
        break;
    }
  }

  /**
   * Get recent failure count
   */
  private getRecentFailures(): number {
    const fiveMinutesAgo = Date.now() - 300000;
    return this.events.filter(
      event => event.type === 'AUTH_FAILURE' && event.timestamp > fiveMinutesAgo
    ).length;
  }

  /**
   * Handle critical security alerts
   */
  private handleCriticalAlert(alert: SecurityAlert): void {
    // Activate emergency protection
    AuthProtection.emergencyProtect(`Critical alert: ${alert.message}`);
    
    // Log to external monitoring (if available)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'security_alert', {
        alert_level: alert.level,
        alert_message: alert.message
      });
    }
  }

  /**
   * Perform periodic health check
   */
  private performHealthCheck(): void {
    const isHealthy = this.isHealthy();
    
    if (!isHealthy) {
      this.createAlert(
        'MEDIUM',
        'Auth system health check failed',
        this.getHealthMetrics()
      );
    }
  }
}

// Export singleton instance
export const authMonitoring = AuthMonitoring.getInstance();

// Export types
export type { AuthMonitoringEvent, AuthHealthMetrics, SecurityAlert, SecurityAlertLevel };

// Prevent modifications
Object.freeze(authMonitoring);